import { getSessionData } from '@confluence/session-data/entry-points/SessionData';
import type { SessionDataType } from '@confluence/session-data/entry-points/SessionDataTypes';

import { initializeClientFromValues } from '../entry-points/initializeClientFromValues';
import { initializeClientWithProvider } from '../entry-points/initializeClientWithProvider';

function getMetadata(name: string) {
	return document.querySelector(`meta[name="${name}"]`)?.getAttribute('content') || '';
}

let statsigInitialized: (value: true) => void;
const statsigInitializedPromise = new Promise<true>((resolve) => {
	statsigInitialized = resolve;
});

export async function waitUntilStatsigInitialized(): Promise<true> {
	return statsigInitializedPromise;
}

export async function getSessionDataAndInitializeStatsig(): Promise<SessionDataType> {
	let sessionData: SessionDataType;

	if (window.__SSR_RENDERED__) {
		// When SSRed getSessionData is synchronous
		sessionData = await getSessionData();
		const { userId, cloudId, orgId, environment } = sessionData;

		// __STATSIG_BOOTSTRAP__ is from cc-frontend-ssr or bifrost template
		const bootstrappedOptions = window.__STATSIG_BOOTSTRAP__;
		if (bootstrappedOptions) {
			const { clientSdkKey, experimentValues, configCollectionValues } = bootstrappedOptions;
			// Although this function returns a promise, it is actually synchronous when experimentValues are provided
			await initializeClientFromValues({
				identifiers: {
					userId,
					tenantId: cloudId || '',
					orgId,
				},
				environment,
				clientSdkKey,
				experimentValues,
				configCollectionValues,
			});
		} else {
			await initializeClientWithProvider({ cloudId, userId, environment, orgId });
		}
	} else {
		const cloudId = getMetadata('ajs-cloud-id');
		const userId = getMetadata('ajs-atlassian-account-id');
		const environment = getMetadata('ajs-environment');
		const orgId = getMetadata('ajs-org-id');

		if (userId && cloudId && environment) {
			// Initialize client in parallel if we have all the metadata
			const promiseAllResult = await Promise.all([
				getSessionData(),
				initializeClientWithProvider({ cloudId, userId, environment, orgId }),
			]);
			sessionData = promiseAllResult[0];
		} else {
			// If we don't have all the info. Eg in standalone mode there is no environment metadata
			// Downgrade to waterfall
			sessionData = await getSessionData();
			await initializeClientWithProvider({
				cloudId: sessionData.cloudId,
				userId: sessionData.userId,
				environment: sessionData.environment,
				orgId: sessionData.orgId,
			});
		}
	}

	statsigInitialized(true);
	return sessionData;
}
